import { useState } from 'react';
import {
  usePatchApiV1PecegepayBilletCancelCoursefinpersonpaymentid,
  usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidPay,
  usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidRefund,
  usePostApiV1PecegepayBilletCoursefinpersonpaymentid,
} from '@/shared/api/generated';
import type { BankBilletViewModel } from '@/shared/api/generated/types';

export interface BilletResult {
  readonly billet: BankBilletViewModel;
  readonly courseFinPersonPaymentId: number;
}

/**
 * Hook de ações financeiras por parcela via PecegePay.
 *
 * Encapsula as operações de gerar/cancelar boleto e pagar/estornar cartão.
 * Os métodos só devem ser chamados quando `courseFinPersonPaymentId` estiver
 * disponível na parcela (preenchido com dados reais da API).
 *
 * @param onSuccess - Callback chamado após cada operação bem-sucedida
 */
export function useParcelActions(onSuccess?: () => void) {
  const [generatedBillet, setGeneratedBillet] = useState<BilletResult | null>(null);

  const generateBilletMutation = usePostApiV1PecegepayBilletCoursefinpersonpaymentid({
    mutation: {
      onSuccess: (data, variables) => {
        if (data) {
          setGeneratedBillet({
            billet: data,
            courseFinPersonPaymentId: variables.courseFinPersonPaymentId,
          });
        }
        onSuccess?.();
      },
    },
  });

  const cancelBilletMutation = usePatchApiV1PecegepayBilletCancelCoursefinpersonpaymentid({
    mutation: onSuccess ? { onSuccess } : {},
  });

  const payCreditCardMutation = usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidPay({
    mutation: onSuccess ? { onSuccess } : {},
  });

  const refundCreditCardMutation = usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidRefund({
    mutation: onSuccess ? { onSuccess } : {},
  });

  return {
    generateBillet: (courseFinPersonPaymentId: number) =>
      generateBilletMutation.mutate({ courseFinPersonPaymentId }),

    cancelBillet: (courseFinPersonPaymentId: number) =>
      cancelBilletMutation.mutate({ courseFinPersonPaymentId }),

    payCreditCard: (courseFinPersonPaymentId: number) =>
      payCreditCardMutation.mutate({ courseFinPersonPaymentId }),

    refundCreditCard: (courseFinPersonPaymentId: number) =>
      refundCreditCardMutation.mutate({ courseFinPersonPaymentId }),

    generatedBillet,
    clearGeneratedBillet: () => setGeneratedBillet(null),

    isGeneratingBillet: generateBilletMutation.isPending,
    isCancellingBillet: cancelBilletMutation.isPending,
    isPayingCreditCard: payCreditCardMutation.isPending,
    isRefundingCreditCard: refundCreditCardMutation.isPending,
  } as const;
}
